import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AppToast from './AppToast';
import { managementAPI } from '../services/api';
import { formatLocalDateYmd } from '../utils/localCalendarDate';
import { Icons } from './Icons';
import { useDataContext } from '../contexts/DataContext';

const OwnerEmployees = ({ selectedCompanyId }) => {
  const weekdayLabel = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const { 
    getEmployeesPageBundle, 
    invalidateCache, 
    isLoading: isDataLoading,
  } = useDataContext();
  const [employees, setEmployees] = useState([]);
  const [sites, setSites] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [siteEmployeesBySite, setSiteEmployeesBySite] = useState({});
  const [shiftsBySite, setShiftsBySite] = useState({});
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [shiftAssignmentsByFactoryEmployee, setShiftAssignmentsByFactoryEmployee] = useState({});
  const [savingShiftByFactoryEmployee, setSavingShiftByFactoryEmployee] = useState({});
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    employee_code: '',
    job_title: '',
    contract_id: '',
  });
  const [editingId, setEditingId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  /** Vista de detalle del empleado (pantalla completa, no modal) */
  const [employeeDetailActive, setEmployeeDetailActive] = useState(false);
  const [selectedDetailSiteId, setSelectedDetailSiteId] = useState('');
  const [loading, setLoading] = useState(false);
  const [savingEmployee, setSavingEmployee] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'active', 'inactive'

  const selectedEmployee = useMemo(
    () => employees.find((e) => String(e.user_id) === selectedEmployeeId),
    [employees, selectedEmployeeId]
  );
  const hasSelectedEmployee = Boolean(selectedEmployee);

  const contractNameById = useMemo(() => {
    const m = {};
    (contracts || []).forEach((c) => {
      m[String(c.id)] = c.name;
    });
    return m;
  }, [contracts]);

  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) => {
      const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (emp.employee_code && emp.employee_code.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = filterStatus === 'all' || 
                           (filterStatus === 'active' && emp.active) ||
                           (filterStatus === 'inactive' && !emp.active);
      return matchesSearch && matchesStatus;
    });
  }, [employees, searchTerm, filterStatus]);
  const loadData = React.useCallback(async (companyIdParam) => {
    const companyId = companyIdParam || selectedCompanyId || '';
    if (!companyId) {
      setEmployees([]);
      setSites([]);
      setContracts([]);
      return;
    }
    
    const bundle = await getEmployeesPageBundle(companyId);
    if (bundle) {
      setEmployees(bundle.employees || []);
      setSites(bundle.sites || []);
      setContracts(bundle.contracts || []);
      setSiteEmployeesBySite(bundle.factory_employees_by_site || {});
      // Note: shifts_by_site is not included in the light bundle
      // Shifts will be loaded on-demand when needed
    }
  }, [selectedCompanyId, getEmployeesPageBundle]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadData();
      } catch (e) {
        setErr(e?.response?.data?.detail || 'No se pudo cargar empleados');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadData]);

  // Combine local loading with data context loading
  const isLoadingData = loading || isDataLoading(selectedCompanyId);

  const assignmentRows = useMemo(() => {
    if (!selectedEmployeeId) return [];
    const rows = [];
    for (const site of sites) {
      const assigned = (siteEmployeesBySite[String(site.id)] || []).find(
        (r) => r.user_id === Number(selectedEmployeeId)
      );
      if (assigned) rows.push({ site, assigned });
    }
    return rows;
  }, [selectedEmployeeId, sites, siteEmployeesBySite]);

  const getAssignedSiteIdsForEmployee = (employeeId) => {
    if (!employeeId) return [];
    const idNum = Number(employeeId);
    return sites
      .filter((site) =>
        (siteEmployeesBySite[String(site.id)] || []).some((row) => Number(row.user_id) === idNum && row.active)
      )
      .map((site) => String(site.id));
  };

  useEffect(() => {
    if (!selectedEmployeeId || assignmentRows.length === 0) {
      setSelectedDetailSiteId('');
      return;
    }
    const exists = assignmentRows.some(({ site }) => String(site.id) === String(selectedDetailSiteId));
    if (!exists) {
      setSelectedDetailSiteId(String(assignmentRows[0].site.id));
    }
  }, [assignmentRows, selectedEmployeeId, selectedDetailSiteId]);

  // Load shift assignments for employee when modal is opened (lazy loading)
  const loadShiftAssignments = useCallback(async (employeeId, assignmentRows) => {
    if (!assignmentRows.length) {
      setShiftAssignmentsByFactoryEmployee({});
      return;
    }
    try {
      const entries = await Promise.all(
        assignmentRows.map(async ({ assigned }) => {
          const res = await managementAPI.employeeShifts(assigned.id);
          const rows = res.data || [];
          return [
            String(assigned.id),
            {
              rows,
              selectedShiftIds: rows.map((r) => String(r.shift_id)),
            },
          ];
        })
      );
      setShiftAssignmentsByFactoryEmployee(Object.fromEntries(entries));
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudieron cargar turnos asignados');
    }
  }, []);

  // Load shifts for a specific site (lazy loading)
  const loadShiftsForSite = useCallback(async (siteId) => {
    if (!siteId || shiftsBySite[String(siteId)]) return; // Already loaded
    
    try {
      // For now, we'll load shifts when needed
      // In the future, this could be optimized to load from the shifts detail endpoint
      const res = await managementAPI.employeeShiftsDetail(selectedEmployeeId, siteId);
      const shiftsData = res.data || {};
      
      setShiftsBySite(prev => ({
        ...prev,
        [String(siteId)]: shiftsData.shifts || []
      }));
    } catch (e) {
      console.error('Error loading shifts for site:', e);
    }
  }, [selectedEmployeeId, shiftsBySite]);

  // Load shifts for selected detail site when it changes (vista detalle)
  useEffect(() => {
    if (selectedDetailSiteId && employeeDetailActive) {
      loadShiftsForSite(selectedDetailSiteId);
    }
  }, [selectedDetailSiteId, employeeDetailActive, loadShiftsForSite]);

  const closeFormModal = () => {
    setForm({ name: '', email: '', password: '', employee_code: '', job_title: '', contract_id: '' });
    setEditingId(null);
    setShowFormModal(false);
  };

  const saveEmployee = async () => {
    if (!selectedCompanyId) return setErr('Primero selecciona una empresa en el header');
    if (!form.name.trim() || !form.email.trim()) return;
    try {
      setSavingEmployee(true);
      setErr('');
      setMsg('');
      if (editingId) {
        const payload = {
          name: form.name.trim(),
          email: form.email.trim(),
          employee_code: form.employee_code.trim() || null,
          job_title: form.job_title.trim() || null,
          contract_id: form.contract_id === '' ? null : Number(form.contract_id),
        };
        if (form.password.trim()) payload.password = form.password.trim();
        const res = await managementAPI.updateEmployee(editingId, Number(selectedCompanyId), payload);
        const row = res.data;
        setForm((f) => ({
          ...f,
          password: '',
          name: row.name,
          email: row.email,
          employee_code: row.employee_code || '',
          job_title: row.job_title || '',
          contract_id: row.contract_id != null ? String(row.contract_id) : '',
        }));
        setMsg('Empleado actualizado');
        invalidateCache(selectedCompanyId);
        await loadData(selectedCompanyId);
        if (showFormModal) closeFormModal();
        return;
      }
      await managementAPI.createEmployee({
        company_id: Number(selectedCompanyId),
        name: form.name.trim(),
        email: form.email.trim(),
        password: form.password.trim() || 'password123',
        employee_code: form.employee_code.trim() || null,
        job_title: form.job_title.trim() || null,
        contract_id: form.contract_id === '' ? null : Number(form.contract_id),
      });
      setMsg('Empleado creado');
      closeFormModal();
      invalidateCache(selectedCompanyId);
      await loadData(selectedCompanyId);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo guardar empleado');
    } finally {
      setSavingEmployee(false);
    }
  };

  const openEmployeeDetail = async (emp) => {
    setShiftsBySite({});
    setShiftAssignmentsByFactoryEmployee({});
    setSelectedEmployeeId(String(emp.user_id));
    setEditingId(emp.user_id);
    setForm({
      name: emp.name || '',
      email: emp.email || '',
      password: '',
      employee_code: emp.employee_code || '',
      job_title: emp.job_title || '',
      contract_id: emp.contract_id != null ? String(emp.contract_id) : '',
    });
    setEmployeeDetailActive(true);

    // Load shift assignments for this employee (lazy loading)
    const employeeAssignments = sites
      .map(site => {
        const assigned = (siteEmployeesBySite[String(site.id)] || []).find(
          (r) => r.user_id === emp.user_id
        );
        return assigned ? { site, assigned } : null;
      })
      .filter(Boolean);

    if (employeeAssignments.length > 0) {
      loadShiftAssignments(emp.user_id, employeeAssignments);
    }
  };

  const closeEmployeeDetail = () => {
    setEmployeeDetailActive(false);
    setSelectedEmployeeId('');
    setEditingId(null);
    setForm({ name: '', email: '', password: '', employee_code: '', job_title: '', contract_id: '' });
    setSelectedDetailSiteId('');
    setShiftAssignmentsByFactoryEmployee({});
    setShiftsBySite({});
  };

  const deactivateEmployee = async (userId) => {
    if (!selectedCompanyId) return;
    try {
      setErr('');
      setMsg('');
      await managementAPI.deactivateEmployee(userId, Number(selectedCompanyId));
      invalidateCache(selectedCompanyId);
      await loadData(selectedCompanyId);
      if (String(userId) === selectedEmployeeId) {
        closeEmployeeDetail();
      }
      setMsg('Empleado dado de baja');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo dar de baja');
    }
  };

  const assignEmployee = async () => {
    if (!selectedEmployeeId || selectedSiteIds.length === 0) return;
    try {
      setErr('');
      setMsg('');
      const selectedSitesAsNumbers = selectedSiteIds.map((id) => Number(id));
      await managementAPI.assignFactoryEmployeesBulk({
        user_id: Number(selectedEmployeeId),
        factory_ids: selectedSitesAsNumbers,
        is_supervisor: false,
        active: true,
      });
      invalidateCache(selectedCompanyId);
      await loadData(selectedCompanyId);
      setMsg(
        selectedSitesAsNumbers.length > 1
          ? `Empleado asignado a ${selectedSitesAsNumbers.length} sedes`
          : 'Empleado asignado a sede'
      );
      setShowAssignModal(false);
      setSelectedSiteIds([]);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudo asignar empleado');
    }
  };

  const toggleSiteSelection = (siteId) => {
    setSelectedSiteIds((prev) => {
      const key = String(siteId);
      if (prev.includes(key)) return prev.filter((id) => id !== key);
      return [...prev, key];
    });
  };

  const getShiftGroupsForSite = (siteId) => {
    const rows = shiftsBySite[String(siteId)] || [];
    const grouped = rows.reduce((acc, row) => {
      const key = row.name;
      if (!acc[key]) acc[key] = [];
      acc[key].push(row);
      return acc;
    }, {});
    return Object.entries(grouped).map(([name, items]) => ({
      name,
      items: [...items].sort((a, b) => a.weekday - b.weekday || a.start_time.localeCompare(b.start_time)),
      shiftIds: items.map((i) => String(i.id)),
    }));
  };

  const toggleShiftGroupSelection = (factoryEmployeeId, siteId, groupName) => {
    const key = String(factoryEmployeeId);
    const groups = getShiftGroupsForSite(siteId);
    const group = groups.find((g) => g.name === groupName);
    if (!group) return;
    const groupIds = group.shiftIds;
    setShiftAssignmentsByFactoryEmployee((prev) => {
      const curr = prev[key] || { rows: [], selectedShiftIds: [] };
      const currentlySelected = curr.selectedShiftIds || [];
      const allSelected = groupIds.every((id) => currentlySelected.includes(id));
      const nextSelected = allSelected
        ? currentlySelected.filter((id) => !groupIds.includes(id))
        : Array.from(new Set([...currentlySelected, ...groupIds]));
      return { ...prev, [key]: { ...curr, selectedShiftIds: nextSelected } };
    });
  };

  const saveShiftsForAssignment = async (factoryEmployeeId, siteId) => {
    const key = String(factoryEmployeeId);
    const state = shiftAssignmentsByFactoryEmployee[key] || { rows: [], selectedShiftIds: [] };
    const existingShiftIds = (state.rows || []).map((r) => String(r.shift_id));
    const targetShiftIds = state.selectedShiftIds || [];
    const toCreate = targetShiftIds.filter((id) => !existingShiftIds.includes(id));
    const toDeleteRows = (state.rows || []).filter((r) => !targetShiftIds.includes(String(r.shift_id)));

    try {
      setSavingShiftByFactoryEmployee((prev) => ({ ...prev, [key]: true }));
      setErr('');
      setMsg('');
      await Promise.all([
        ...toDeleteRows.map((r) => managementAPI.deleteEmployeeShift(r.id)),
        ...toCreate.map((shiftId) =>
          managementAPI.assignEmployeeShift({
            factory_employee_id: Number(factoryEmployeeId),
            shift_id: Number(shiftId),
            effective_from: formatLocalDateYmd(),
          })
        ),
      ]);
      const refreshed = await managementAPI.employeeShifts(Number(factoryEmployeeId));
      const rows = refreshed.data || [];
      setShiftAssignmentsByFactoryEmployee((prev) => ({
        ...prev,
        [key]: { rows, selectedShiftIds: rows.map((r) => String(r.shift_id)) },
      }));
      const siteName = sites.find((s) => s.id === Number(siteId))?.name || 'sede';
      setMsg(`Turnos actualizados para ${siteName}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'No se pudieron guardar los turnos');
    } finally {
      setSavingShiftByFactoryEmployee((prev) => ({ ...prev, [key]: false }));
    }
  };

  const selectedDetailAssignment = assignmentRows.find(({ site }) => String(site.id) === String(selectedDetailSiteId));

  return (
    <div className="space-y-6">
      {!employeeDetailActive && (
      <>
      {/* Header Section */}
      <div className="flex-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Empleados</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gestiona tu equipo, asignaciones y horarios de trabajo
          </p>
        </div>
        <button
          className="btn-mobile btn-primary flex items-center gap-2"
          onClick={() => {
            setErr('');
            setMsg('');
            setSelectedEmployeeId('');
            setEditingId(null);
            setForm({ name: '', email: '', password: '', employee_code: '', job_title: '', contract_id: '' });
            setShowFormModal(true);
          }}
          disabled={loading || !selectedCompanyId}
        >
          <Icons.Plus size={16} />
          Agregar Empleado
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-mobile">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Icons.Users size={24} className="text-primary-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Empleados</p>
              <p className="text-2xl font-semibold text-gray-900">{employees.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card-mobile">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <Icons.CheckCircle size={24} className="text-success-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Activos</p>
              <p className="text-2xl font-semibold text-gray-900">
                {employees.filter(e => e.active).length}
              </p>
            </div>
          </div>
        </div>

        <div className="card-mobile">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-warning-100 rounded-lg flex items-center justify-center">
              <Icons.Building2 size={24} className="text-warning-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Sedes Totales</p>
              <p className="text-2xl font-semibold text-gray-900">{sites.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card-mobile">
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex-1 min-w-0 max-w-md">
            <div className="relative">
              <Icons.Users size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar empleados..."
                className="input-mobile pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <select 
              className="input-mobile text-sm min-w-0"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="all">Todos los estados</option>
              <option value="active">Solo activos</option>
              <option value="inactive">Solo inactivos</option>
            </select>
            
            <div className="border border-gray-300 rounded-lg p-1 flex">
              <button
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'grid' 
                    ? 'bg-primary-500 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setViewMode('grid')}
              >
                Grid
              </button>
              <button
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  viewMode === 'list' 
                    ? 'bg-primary-500 text-white' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                onClick={() => setViewMode('list')}
              >
                Lista
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Employees Display */}
      <div className="card-mobile">
        {isLoadingData ? (
          <div className="flex items-center justify-center py-12">
            <div className="loading-spinner lg"></div>
          </div>
        ) : filteredEmployees.length === 0 ? (
          <div className="text-center py-12">
            <Icons.Users size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No se encontraron empleados' : 'No hay empleados'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm 
                ? 'Intenta con otros términos de búsqueda'
                : 'Comienza agregando empleados a tu empresa'
              }
            </p>
            {!searchTerm && (
              <button
                className="btn-mobile btn-primary"
                onClick={() => setShowFormModal(true)}
                disabled={!selectedCompanyId}
              >
                <Icons.Plus size={16} className="mr-2" />
                Agregar Primer Empleado
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredEmployees.map((emp) => (
              <div
                key={emp.user_id}
                className="border rounded-xl p-4 transition-all cursor-pointer hover:shadow-md border-gray-200 hover:border-gray-300"
                onClick={() => openEmployeeDetail(emp)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="list-item-avatar">
                    {emp.name.charAt(0).toUpperCase()}
                  </div>
                  <span className={`status-badge ${emp.active ? 'success' : 'danger'}`}>
                    {emp.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                
                <h3 className="font-semibold text-gray-900 mb-1">{emp.name}</h3>
                <p className="text-sm text-gray-600 mb-1">{emp.email}</p>
                {emp.job_title && (
                  <p className="text-sm text-gray-500 mb-2">{emp.job_title}</p>
                )}
                <p className="text-xs text-gray-500 mb-2">
                  Contrato: {emp.contract_id ? contractNameById[String(emp.contract_id)] || `#${emp.contract_id}` : '—'}
                </p>
                {emp.employee_code && (
                  <p className="text-xs text-gray-500">Código: {emp.employee_code}</p>
                )}
                
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    {getAssignedSiteIdsForEmployee(emp.user_id).length} sedes asignadas
                  </span>
                  <Icons.User size={16} className="text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Empleado</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Contacto</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Puesto</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Contrato</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Estado</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-900">Sedes</th>
                  <th className="text-right py-3 px-4 font-semibold text-gray-900">Acción</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((emp) => (
                  <tr
                    key={emp.user_id}
                    className="border-b border-gray-100 transition-colors hover:bg-gray-50"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="list-item-avatar">
                          {emp.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{emp.name}</p>
                          {emp.employee_code && (
                            <p className="text-xs text-gray-500">#{emp.employee_code}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-900">{emp.email}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-900">{emp.job_title || '-'}</p>
                    </td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-900">
                        {emp.contract_id
                          ? contractNameById[String(emp.contract_id)] || `#${emp.contract_id}`
                          : '—'}
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`status-badge ${emp.active ? 'success' : 'danger'}`}>
                        {emp.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="text-sm text-gray-600">
                        {getAssignedSiteIdsForEmployee(emp.user_id).length} sedes
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        className="btn-mobile btn-secondary btn-sm"
                        onClick={() => openEmployeeDetail(emp)}
                      >
                        Ver Detalle
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      </>
      )}

      {/* Create Employee Modal */}
      {showFormModal && (
        <div className="modal-overlay" onClick={closeFormModal}>
          <div className="modal-mobile center max-w-3xl w-[90vw]" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Nuevo Empleado</h3>
              <button onClick={closeFormModal}>
                <Icons.X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="form-mobile">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Nombre Completo</label>
                    <input 
                      className="input-mobile" 
                      placeholder="Nombre completo del empleado"
                      value={form.name} 
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} 
                    />
                  </div>
                  
                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Email Corporativo</label>
                    <input 
                      className="input-mobile" 
                      placeholder="correo@ejemplo.com"
                      type="email"
                      value={form.email} 
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} 
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Contraseña Temporal</label>
                    <input 
                      className="input-mobile" 
                      placeholder="Mínimo 6 caracteres"
                      type="password"
                      value={form.password} 
                      onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} 
                    />
                    <p className="form-description">El empleado podrá cambiarla después del primer login</p>
                  </div>
                  
                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Código de Empleado</label>
                    <input 
                      className="input-mobile" 
                      placeholder="ej. EMP001, 12345..."
                      value={form.employee_code} 
                      onChange={(e) => setForm((f) => ({ ...f, employee_code: e.target.value }))} 
                    />
                    <p className="form-description">Código único para identificación (opcional)</p>
                  </div>
                  
                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Puesto de Trabajo</label>
                    <input 
                      className="input-mobile" 
                      placeholder="ej. Limpieza, Jardinería..."
                      value={form.job_title} 
                      onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))} 
                    />
                    <p className="form-description">Descripción del rol (opcional)</p>
                  </div>
                </div>
                
                <div className="border-t border-gray-200 pt-6">
                  <div className="form-group-mobile">
                    <label className="form-label-mobile">Contrato colectivo</label>
                    <select
                      className="input-mobile"
                      value={form.contract_id}
                      onChange={(e) => setForm((f) => ({ ...f, contract_id: e.target.value }))}
                    >
                      <option value="">Sin contrato</option>
                      {contracts.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                          {!c.active ? ' (inactivo)' : ''}
                        </option>
                      ))}
                    </select>
                    <p className="form-description">Opcional. Necesario para asignar turnos por contrato.</p>
                  </div>
                  <button 
                    className="btn-mobile btn-primary w-full btn-lg" 
                    onClick={saveEmployee} 
                    disabled={loading || !selectedCompanyId || !form.name.trim() || !form.email.trim()}
                  >
                    <Icons.Plus size={18} className="mr-2" />
                    Crear Empleado
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Site Assignment Modal */}
      {showAssignModal && selectedEmployeeId && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal-mobile center max-w-2xl w-[85vw]" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Asignar Sedes</h3>
              <button onClick={() => { setShowAssignModal(false); setSelectedSiteIds([]); }}>
                <Icons.X size={20} />
              </button>
            </div>
            <div className="modal-content">
              <div className="space-y-4">
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="list-item-avatar">
                    {selectedEmployee?.name?.charAt(0).toUpperCase() || 'E'}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{selectedEmployee?.name}</p>
                    <p className="text-sm text-gray-600">{selectedEmployee?.email}</p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-gray-900 mb-3">Seleccionar Sedes</h4>
                  {sites.length === 0 ? (
                    <p className="text-sm text-gray-600 text-center py-4">No hay sedes disponibles</p>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {sites.map((site) => {
                        const checked = selectedSiteIds.includes(String(site.id));
                        return (
                          <label
                            key={site.id}
                            className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                              checked 
                                ? 'bg-primary-50 border border-primary-200' 
                                : 'hover:bg-gray-50 border border-gray-200'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSiteSelection(site.id)}
                              className="mt-1"
                            />
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Icons.Building2 size={16} className="text-gray-500 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900">{site.name}</p>
                                <p className="text-sm text-gray-600 truncate">{site.address}</p>
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>

                <button 
                  className="btn-mobile btn-primary w-full" 
                  onClick={assignEmployee} 
                  disabled={loading || !selectedEmployeeId || selectedSiteIds.length === 0}
                >
                  <Icons.Check size={16} className="mr-2" />
                  Confirmar Asignación ({selectedSiteIds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detalle de empleado (vista propia, no modal) */}
      {employeeDetailActive && hasSelectedEmployee && (
        <div className="space-y-6">
          <div className="card-mobile">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
              <div className="flex flex-wrap items-center gap-3 min-w-0">
                <button
                  type="button"
                  className="btn-mobile btn-secondary shrink-0"
                  onClick={closeEmployeeDetail}
                >
                  ← Volver al listado
                </button>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center shrink-0">
                    <span className="text-lg font-semibold text-primary-600">
                      {selectedEmployee.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-xl font-semibold text-gray-900 truncate">{selectedEmployee.name}</h2>
                    <p className="text-sm text-gray-600 truncate">{selectedEmployee.email}</p>
                  </div>
                </div>
              </div>
              <span className={`status-badge shrink-0 ${selectedEmployee.active ? 'success' : 'danger'}`}>
                {selectedEmployee.active ? 'Activo' : 'Inactivo'}
              </span>
            </div>
          </div>

          <div className="card-mobile pb-8">
            <div className="space-y-8">
                {/* Employee Form */}
                <div className="form-section">
                  <h4 className="form-section-title">Información Personal</h4>
                  <div className="form-mobile">
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
                      <div className="form-group-mobile">
                        <label className="form-label-mobile">Nombre Completo</label>
                        <input
                          className="input-mobile"
                          placeholder="Nombre completo"
                          value={form.name}
                          onChange={(ev) => setForm((f) => ({ ...f, name: ev.target.value }))}
                        />
                      </div>
                      <div className="form-group-mobile">
                        <label className="form-label-mobile">Email</label>
                        <input
                          className="input-mobile"
                          placeholder="correo@ejemplo.com"
                          type="email"
                          value={form.email}
                          onChange={(ev) => setForm((f) => ({ ...f, email: ev.target.value }))}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      <div className="form-group-mobile">
                        <label className="form-label-mobile">Puesto de Trabajo</label>
                        <input
                          className="input-mobile"
                          placeholder="ej. Supervisor, Operario..."
                          value={form.job_title}
                          onChange={(ev) => setForm((f) => ({ ...f, job_title: ev.target.value }))}
                        />
                      </div>
                      <div className="form-group-mobile">
                        <label className="form-label-mobile">Código de Empleado</label>
                        <input
                          className="input-mobile"
                          placeholder="Código único (opcional)"
                          value={form.employee_code}
                          onChange={(ev) => setForm((f) => ({ ...f, employee_code: ev.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="form-group-mobile lg:max-w-md">
                      <label className="form-label-mobile">Contrato colectivo</label>
                      <select
                        className="input-mobile"
                        value={form.contract_id}
                        onChange={(ev) => setForm((f) => ({ ...f, contract_id: ev.target.value }))}
                      >
                        <option value="">Sin contrato</option>
                        {contracts.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                            {!c.active ? ' (inactivo)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group-mobile">
                      <label className="form-label-mobile">Nueva Contraseña</label>
                      <input
                        className="input-mobile"
                        placeholder="Dejar en blanco para mantener actual"
                        type="password"
                        autoComplete="new-password"
                        value={form.password}
                        onChange={(ev) => setForm((f) => ({ ...f, password: ev.target.value }))}
                      />
                      <p className="form-description">Solo completar si deseas cambiar la contraseña</p>
                    </div>

                    <div className="pt-4 pb-2">
                      <div className="flex gap-3">
                        <button
                          className="btn-mobile btn-primary flex-1"
                          onClick={saveEmployee}
                          disabled={loading || savingEmployee || !selectedCompanyId}
                        >
                          {savingEmployee ? (
                            <>
                              <div className="loading-spinner sm mr-2" />
                              Guardando...
                            </>
                          ) : (
                            <>
                              <Icons.Check size={16} className="mr-2" />
                              Guardar Cambios
                            </>
                          )}
                        </button>
                        {selectedEmployee?.active && (
                          <button
                            className="btn-mobile btn-danger"
                            onClick={() => {
                              if (!window.confirm('¿Dar de baja a este empleado? No podrá ingresar hasta que lo reactives.')) return;
                              deactivateEmployee(selectedEmployee.user_id);
                            }}
                          >
                            Dar de Baja
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Site Assignments */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                  <div className="form-section">
                    <div className="flex-between mb-4">
                      <h4 className="form-section-title m-0">Sedes Asignadas</h4>
                      <button
                        className="btn-mobile btn-secondary btn-sm"
                        onClick={() => {
                          setSelectedSiteIds(getAssignedSiteIdsForEmployee(selectedEmployeeId));
                          setShowAssignModal(true);
                        }}
                      >
                        <Icons.Plus size={16} className="mr-2" />
                        Gestionar
                      </button>
                    </div>
                    
                    {assignmentRows.length === 0 ? (
                      <div className="text-center py-8">
                        <Icons.Building2 size={32} className="mx-auto text-gray-400 mb-3" />
                        <p className="text-sm text-gray-600">Sin sedes asignadas</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {assignmentRows.map(({ site, assigned }) => {
                          const active = String(site.id) === String(selectedDetailSiteId);
                          return (
                            <button
                              key={assigned.id}
                              className={`w-full text-left p-3 rounded-lg border transition-all ${
                                active 
                                  ? 'border-primary-300 bg-primary-50' 
                                  : 'border-gray-200 hover:border-gray-300 bg-white'
                              }`}
                              onClick={() => setSelectedDetailSiteId(String(site.id))}
                            >
                              <div className="flex items-center gap-3">
                                <Icons.Building2 size={16} className={active ? 'text-primary-600' : 'text-gray-500'} />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900">{site.name}</p>
                                  <p className="text-xs text-gray-500 truncate">{site.address}</p>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Shift Management */}
                  <div className="form-section">
                    <h4 className="form-section-title">Gestión de Turnos</h4>
                    {!selectedDetailAssignment ? (
                      <div className="text-center py-8">
                        <Icons.Clock size={32} className="mx-auto text-gray-400 mb-3" />
                        <p className="text-sm text-gray-600">Selecciona una sede para gestionar turnos</p>
                      </div>
                    ) : (() => {
                      const { site, assigned } = selectedDetailAssignment;
                      const shifts = shiftsBySite[String(site.id)] || [];
                      const shiftGroups = getShiftGroupsForSite(site.id);
                      const state = shiftAssignmentsByFactoryEmployee[String(assigned.id)] || { rows: [], selectedShiftIds: [] };
                      
                      return (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Icons.Building2 size={16} />
                            <span>{site.name}</span>
                          </div>

                          {shifts.length === 0 ? (
                            <div className="text-center py-6">
                              <Icons.Clock size={24} className="mx-auto text-gray-400 mb-2" />
                              <p className="text-sm text-gray-600">No hay turnos configurados para esta sede</p>
                            </div>
                          ) : (
                            <div className="space-y-3">
                              {shiftGroups.map((group) => {
                                const checked = group.shiftIds.every((id) => state.selectedShiftIds.includes(id));
                                return (
                                  <label
                                    key={`${site.id}-${group.name}`}
                                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                                      checked 
                                        ? 'bg-primary-50 border border-primary-200' 
                                        : 'hover:bg-gray-50 border border-gray-200'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      className="mt-1"
                                      checked={checked}
                                      onChange={() => toggleShiftGroupSelection(assigned.id, site.id, group.name)}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-gray-900">{group.name}</p>
                                      <p className="text-sm text-gray-600">
                                        {group.items.map((s) => 
                                          `${weekdayLabel[s.weekday]} ${s.start_time}-${s.end_time}`
                                        ).join(' • ')}
                                      </p>
                                    </div>
                                  </label>
                                );
                              })}
                              
                              <div className="pt-4 pb-6">
                                <button
                                  className="btn-mobile btn-primary w-full"
                                  onClick={() => saveShiftsForAssignment(assigned.id, site.id)}
                                  disabled={savingShiftByFactoryEmployee[String(assigned.id)]}
                                >
                                  {savingShiftByFactoryEmployee[String(assigned.id)] ? (
                                    <>
                                      <div className="loading-spinner sm mr-2" />
                                      Guardando...
                                    </>
                                  ) : (
                                    <>
                                      <Icons.Check size={16} className="mr-2" />
                                      Guardar Turnos
                                    </>
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
      )}

      <AppToast
        message={err || msg}
        variant={err ? 'error' : 'success'}
        onClose={() => {
          setErr('');
          setMsg('');
        }}
      />
    </div>
  );
};

export default OwnerEmployees;